import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../../api/axiosInstance";

const BASE = "dashboard/database-backup";
const CHUNKED_URL = `${BASE}/chunked`;

/**
 * Create a full database backup using the chunked API.
 * POST /api/dashboard/database-backup/chunked
 * First call without backup_id to start; then loop with backup_id until done: true.
 * Dispatches setChunkedProgress so the UI can show progress (current_index / total).
 */
export const createChunkedBackupFunc = createAsyncThunk(
  "databaseBackupSlicer/createChunkedBackupFunc",
  async (_, thunkAPI) => {
    const { rejectWithValue, dispatch } = thunkAPI;
    try {
      // 1) Start session: POST without backup_id
      const startRes = await api.post(CHUNKED_URL, {});
      let data = startRes.data?.data ?? startRes.data;
      const backupId = data?.backup_id;
      const total = data?.total ?? 0;
      const currentIndex = data?.current_index ?? 0;

      if (!backupId) {
        return rejectWithValue({ message: "Invalid start response: missing backup_id" });
      }

      dispatch({
        type: "databaseBackupSlicer/setChunkedProgress",
        payload: { backupId, total, currentIndex, done: false },
      });

      // 2) Loop: POST with backup_id until done
      const delay = (ms) => new Promise((r) => setTimeout(r, ms));
      while (true) {
        const stepRes = await api.post(CHUNKED_URL, { backup_id: backupId });
        data = stepRes.data?.data ?? stepRes.data;
        const done = data?.done === true;

        dispatch({
          type: "databaseBackupSlicer/setChunkedProgress",
          payload: {
            backupId,
            total: data?.total ?? total,
            currentIndex: data?.current_index ?? 0,
            done,
            filename: data?.filename,
            download_url: data?.download_url,
          },
        });

        if (done) {
          return {
            backup_id: backupId,
            filename: data?.filename,
            download_url: data?.download_url,
            current_index: data?.current_index,
            total: data?.total,
          };
        }

        await delay(50);
      }
    } catch (error) {
      const payload = error.response?.data || error.message;
      return rejectWithValue(typeof payload === "object" ? payload : { message: payload });
    }
  }
);

/**
 * Download the latest backup file.
 * GET /api/dashboard/database-backup/download (no query params).
 * Backend returns the most recent backup file; 404 if none available.
 * Triggers browser download via blob + object URL.
 */
export const downloadBackupFunc = createAsyncThunk(
  "databaseBackupSlicer/downloadBackupFunc",
  async (_, thunkAPI) => {
    const { rejectWithValue } = thunkAPI;
    try {
      const token = localStorage.getItem("token");
      const baseURL = api.defaults.baseURL;
      const url = `${baseURL}/${BASE}/download`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/sql, application/octet-stream, */*",
          Authorization: token ? `Bearer ${token}` : "",
          "X-Language": localStorage.getItem("langSlug") ?? "en",
        },
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        return rejectWithValue({ message: errData?.message || "No backups available." });
      }
      const blob = await response.blob();
      const disposition = response.headers.get("Content-Disposition");
      const filenameMatch = disposition && /filename[*]?=(?:UTF-8'')?["']?([^"'\s]+)["']?/i.exec(disposition);
      const filename = filenameMatch ? filenameMatch[1].trim() : "database-backup.sql";
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(objectUrl);
      return { filename };
    } catch (error) {
      return rejectWithValue(error?.message || error.response?.data || "Download failed.");
    }
  }
);

const databaseBackupSlicer = createSlice({
  name: "databaseBackupSlicer",
  initialState: {
    create: {
      loading: false,
      data: null,
      error: null,
      chunkedProgress: { backupId: null, total: 0, currentIndex: 0, done: false },
    },
    download: { loading: false, error: null },
  },
  reducers: {
    setChunkedProgress: (state, action) => {
      const p = action.payload || {};
      if (p.backupId !== undefined) state.create.chunkedProgress.backupId = p.backupId;
      if (p.total !== undefined) state.create.chunkedProgress.total = p.total;
      if (p.currentIndex !== undefined) state.create.chunkedProgress.currentIndex = p.currentIndex;
      if (p.done !== undefined) state.create.chunkedProgress.done = p.done;
      if (p.filename !== undefined) state.create.chunkedProgress.filename = p.filename;
      if (p.download_url !== undefined) state.create.chunkedProgress.download_url = p.download_url;
    },
    clearBackupResult: (state) => {
      state.create.data = null;
      state.create.error = null;
      state.create.chunkedProgress = { backupId: null, total: 0, currentIndex: 0, done: false };
    },
  },
  extraReducers: (builder) => {
    // Create chunked backup
    builder
      .addCase(createChunkedBackupFunc.pending, (state) => {
        state.create.loading = true;
        state.create.error = null;
        state.create.data = null;
        state.create.chunkedProgress = { backupId: null, total: 0, currentIndex: 0, done: false };
      })
      .addCase(createChunkedBackupFunc.fulfilled, (state, action) => {
        state.create.loading = false;
        state.create.data = {
          filename: action.payload?.filename,
          download_url: action.payload?.download_url,
          backup_id: action.payload?.backup_id,
        };
        state.create.error = null;
        state.create.chunkedProgress.done = true;
        state.create.chunkedProgress.currentIndex = action.payload?.current_index ?? state.create.chunkedProgress.currentIndex;
        state.create.chunkedProgress.total = action.payload?.total ?? state.create.chunkedProgress.total;
        if (action.payload?.filename) state.create.chunkedProgress.filename = action.payload.filename;
        if (action.payload?.download_url) state.create.chunkedProgress.download_url = action.payload.download_url;
      })
      .addCase(createChunkedBackupFunc.rejected, (state, action) => {
        state.create.loading = false;
        state.create.error = action.payload?.message || action.payload?.errors || "Create backup failed.";
        state.create.data = null;
      });
    // Download backup
    builder
      .addCase(downloadBackupFunc.pending, (state) => {
        state.download.loading = true;
        state.download.error = null;
      })
      .addCase(downloadBackupFunc.fulfilled, (state) => {
        state.download.loading = false;
        state.download.error = null;
      })
      .addCase(downloadBackupFunc.rejected, (state, action) => {
        state.download.loading = false;
        state.download.error = action.payload?.message || action.payload?.errors || "Download failed.";
      });
  },
});

export const { clearBackupResult, setChunkedProgress } = databaseBackupSlicer.actions;
export default databaseBackupSlicer.reducer;
